<template>
  <b-card
      class="file-card h-100"
      @click="$emit('view')"
      @mouseenter="isHovering = true"
      @mouseleave="isHovering = false"
  >
    <div v-if="selectable" class="selection-checkbox" @click.stop>
      <b-form-checkbox
          :checked="selected"
          @change="$emit('select')"
          size="lg"
          class="file-checkbox"
      />
    </div>
    <div class="file-thumb position-relative">
      <!-- Selection Checkbox -->


      <!-- Thumbnail or Fallback Icon -->
      <template v-if="fullThumbnailUrl">
        <b-img :src="fullThumbnailUrl" alt="thumbnail" fluid class="thumbnail-img rounded"/>
      </template>
      <template v-else>
        <feather-icon icon="ApertureIcon" size="50"></feather-icon>
      </template>

      <!-- Preview Video (shown on hover for videos with preview) -->
      <video
          v-if="file.mediaType === 2 && previewUrl"
          ref="previewVideo"
          :src="previewUrl"
          class="preview-video rounded"
          muted
          playsinline
          preload="metadata"
          @loadedmetadata="onVideoLoaded"
      ></video>

      <!-- Badges -->
      <span class="file-type-badge">{{ mediaTypeLabel }}</span>
      <span v-if="isHls" class="hls-badge">HLS</span>
      <span v-if="file.isEncrypted" class="encrypted-badge">رمزنگاری شده</span>
      <span v-if="file.isPublic" class="public-badge">عمومی</span>

      <!-- Status Badge -->
      <span class="status-badge" :style="{ backgroundColor: statusColor }">
        {{ statusLabel }}
      </span>

      <!-- Cool Processing Overlay -->
      <div v-if="file.status === 1" class="processing-overlay">
        <div class="processing-ring"></div>
        <div class="processing-text">در حال رمزنگاری برای عدم دانلود...</div>
        <b-button
            size="sm"
            variant="light"
            class="action-btn"
            @click.stop="$emit('refetch')"
            title="بروزرسانی وضعیت"
        >
          بروزرسانی وضعیت
        </b-button>
        <div class="processing-bar">
          <div class="processing-bar-fill"></div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="file-actions">
        <b-button
            size="sm"
            variant="light"
            class="action-btn"
            @click.stop="$emit('view')"
            title="جزئیات"
        >
          <feather-icon icon="EyeIcon" />
        </b-button>
        <b-button
            size="sm"
            variant="light"
            class="action-btn"
            @click.stop="$emit('edit')"
            title="ویرایش"
            :disabled="file.status === 1"
        >
          <feather-icon icon="EditIcon" />
        </b-button>
        <b-button
            v-if="file.mediaType === 2 && !isHls"
            size="sm"
            variant="light"
            class="action-btn"
            @click.stop="$emit('hls')"
            title="تبدیل به HLS"
            :disabled="file.status === 1"
        >
          <feather-icon icon="FilmIcon" />
        </b-button>
        <b-button
            size="sm"
            variant="danger"
            class="action-btn"
            @click.stop="$emit('delete')"
            title="حذف"
            :disabled="file.status === 1"
        >
          <feather-icon icon="DeleteIcon" />
        </b-button>
      </div>
    </div>

    <div class="file-info">
      <div class="file-title" :title="file.title || file.originalName">
        {{ file.title || file.originalName }}
      </div>
      <div class="file-meta">
        <span>{{ file.size | fileSize }}</span>
        <span>{{ file.createdAt | dateTime }}</span>
      </div>
      <div v-if="file.tags" class="file-tags">
        <span v-for="tag in file.tags.split(',')" :key="tag">{{ tag.trim() }}</span>
      </div>
    </div>
  </b-card>
</template>

<script>
import { BButton, BCard, BImg, BFormCheckbox } from 'bootstrap-vue'
import Helper from '@/libs/Helper'

export default {
  name: 'FileCard',
  components: { BCard, BImg, BButton, BFormCheckbox },
  props: {
    file: { type: Object, required: true },
    selectable: { type: Boolean, default: true },
    selected: { type: Boolean, default: false }
  },
  data() {
    return {
      baseUrl: Helper.baseUrl || 'https://demoapi.lenzava.com',
      isHovering: false,
      videoLoaded: false
    }
  },
  computed: {
    mediaTypeLabel() {
      const map = { 1: 'تصویر', 2: 'ویدیو', 3: 'صوتی', 4: 'سند' }
      return map[this.file.mediaType] || 'ناشناخته'
    },
    fileIcon() {
      const map = { 1: 'fa-image', 2: 'fa-video', 3: 'fa-music', 4: 'fa-file-alt' }
      return map[this.file.mediaType] || 'fa-file'
    },
    isHls() {
      return this.file.streamUrl && this.file.streamUrl.endsWith('.m3u8')
    },
    statusLabel() {
      const map = { 0: 'در انتظار', 1: 'در حال پردازش', 2: 'پردازش شده', 3: 'ناموفق' }
      return map[this.file.status] || 'ناشناخته'
    },
    statusColor() {
      const map = { 0: '#f59e0b', 1: '#3b82f6', 2: '#22c55e', 3: '#ef4444' }
      return map[this.file.status] || '#94a3b8'
    },
    fullThumbnailUrl() {
      if (!this.file?.thumbnailUrl) return null
      const url = this.file.thumbnailUrl
      if (url.startsWith('http://') || url.startsWith('https://')) return url
      const cleanUrl = url.startsWith('/') ? url.substring(1) : url
      return `${this.baseUrl}/${cleanUrl}`
    },
    previewVariant() {
      if (!this.file?.variants || !Array.isArray(this.file.variants)) return null
      const previewVariant = this.file.variants.find(
          v => v.variantType && v.variantType.includes('Preview')
      )
      if (previewVariant) return previewVariant
      const hlsVariant = this.file.variants.find(
          v => v.variantType && v.variantType.startsWith('HLS_')
      )
      return hlsVariant || null
    },
    previewUrl() {
      if (!this.previewVariant) return null
      const path = this.previewVariant.path
      if (path.startsWith('http://') || path.startsWith('https://')) return path
      const cleanPath = path.startsWith('/') ? path.substring(1) : path
      return `${this.baseUrl}/${cleanPath}`
    },
    downloadUrl() {
      if (!this.file?.id) return null
      return `${this.baseUrl}/api/MediaLibrary/download/${this.file.id}`
    }
  },
  watch: {
    isHovering(newVal) {
      const video = this.$refs.previewVideo
      if (!video) return
      if (newVal && this.previewUrl) {
        if (this.videoLoaded) {
          video.currentTime = 0
          video.play().catch(() => {})
        }
      } else {
        video.pause()
        video.currentTime = 0
        this.videoLoaded = false
        video.load()
      }
    }
  },
  methods: {
    onVideoLoaded() {
      this.videoLoaded = true
      if (this.isHovering) {
        const video = this.$refs.previewVideo
        if (video) {
          video.currentTime = 0
          video.play().catch(() => {})
        }
      }
    }
  },
  filters: {
    fileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    dateTime(iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      return d.toLocaleDateString('fa-IR') + ' ' + d.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })
    }
  }
}
</script>

<style scoped>
.file-card {
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
}
.file-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.07);
}

.file-thumb {
  position: relative;
  width: 100%;
  aspect-ratio: 1/1;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 56px;
  color: #94a3b8;
  overflow: hidden;
}

.thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s ease;
}

.preview-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: none;
  background: #0f172a;
  z-index: 1;
}
.file-card:hover .preview-video {
  display: block;
}

/* Badges */
.file-type-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(6px);
  color: #fff;
  padding: 4px 14px;
  border-radius: 30px;
  font-size: 11px;
  font-weight: 600;
  z-index: 5;
}
.hls-badge {
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: rgba(99, 102, 241, 0.9);
  backdrop-filter: blur(6px);
  color: #fff;
  padding: 4px 14px;
  border-radius: 30px;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  z-index: 5;
}
.encrypted-badge {
  position: absolute;
  top: 40px;
  left: 12px;
  background: rgba(239, 68, 68, 0.9);
  backdrop-filter: blur(6px);
  color: #fff;
  padding: 4px 10px;
  border-radius: 30px;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 5;
}
.public-badge {
  position: absolute;
  bottom: 48px;
  left: 12px;
  background: rgba(34, 197, 94, 0.9);
  backdrop-filter: blur(6px);
  color: #fff;
  padding: 4px 10px;
  border-radius: 30px;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 5;
}
.status-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  color: #fff;
  padding: 3px 12px;
  border-radius: 30px;
  font-size: 10px;
  font-weight: 600;
  z-index: 5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Selection Checkbox */
.selection-checkbox {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 15;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  border-radius: 6px;
  padding: 4px 6px;
}
.file-checkbox {
  margin: 0;
}
.file-checkbox >>> .custom-control-label::before,
.file-checkbox >>> .custom-control-label::after {
  width: 1.2rem;
  height: 1.2rem;
}

/* ===== COOL PROCESSING OVERLAY ===== */
.processing-overlay {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(6px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  z-index: 10;
  border-radius: 8px;
  color: #fff;
  animation: overlayIn 0.3s ease;
}

@keyframes overlayIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Modern ring spinner */
.processing-ring {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-top-color: #818cf8;
  border-right-color: #a855f7;
  animation: ringSpin 0.8s linear infinite;
  box-shadow: 0 0 20px rgba(99, 102, 241, 0.3);
}

@keyframes ringSpin {
  to { transform: rotate(360deg); }
}

.processing-text {
  font-size: 13px;
  font-weight: 500;
  opacity: 0.9;
  letter-spacing: 0.3px;
}

/* Animated progress bar at bottom */
.processing-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

.processing-bar-fill {
  height: 100%;
  width: 30%;
  background: linear-gradient(90deg, #818cf8, #a855f7, #818cf8);
  border-radius: 0 2px 2px 0;
  animation: barSlide 1.5s ease-in-out infinite;
}

@keyframes barSlide {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(400%);
  }
}

/* ===== FILE ACTIONS (hover) ===== */
.file-actions {
  position: absolute;
  bottom: 12px;
  left: 5px;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.25s ease;
  z-index: 10;
}
.file-card:hover .file-actions {
  opacity: 1;
}
.file-actions .action-btn {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  border: none;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(6px);
  color: #fff;
}
.file-actions .action-btn:hover:not(:disabled) {
  background: #6366f1;
  transform: scale(1.05);
}
.file-actions .action-btn.btn-danger:hover:not(:disabled) {
  background: #ef4444;
}
.file-actions .action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* File Info */
.file-info {
  padding: 16px 18px 18px;
}
.file-title {
  font-weight: 600;
  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.file-meta {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
}
.file-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
}
.file-tags span {
  background: #f1f5f9;
  padding: 2px 12px;
  border-radius: 20px;
  font-size: 11px;
  color: #475569;
}
</style>
